import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UserPlus, AlertCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import { register, loginWithGoogle } from '@/services/authService';
import { SEO } from '@/components/ui/seo';

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showGoogleLogin, setShowGoogleLogin] = useState(false);

  // Kiểm tra lỗi từ URL khi component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const errorParam = searchParams.get('error');
    
    if (errorParam) {
      switch(errorParam) {
        case 'usernameExists':
          setError(t('usernameExists', { ns: 'auth' }));
          break;
        case 'usernameInvalid':
          setError(t('usernameInvalid', { ns: 'auth' }));
          break;
        case 'usernameLength':
          setError(t('usernameLength', { ns: 'auth' }));
          break;
        case 'invalidDisplayName':
          setError(t('invalidDisplayName', { ns: 'auth' }));
          break;
        case 'displayNameContainsInvalidChars':
          setError(t('displayNameContainsInvalidChars', { ns: 'auth' }));
          break;
        case 'emailInvalid':
          setError(t('emailInvalid', { ns: 'auth' }));
          break;
        case 'emailInUse':
          setError(t('emailInUse', { ns: 'auth' }));
          break;
        case 'databaseError':
          setError(t('databaseError', { ns: 'auth' }));
          setShowGoogleLogin(true);
          break;
        default:
          setError(t('registrationFailed', { ns: 'auth' }));
      }
    }
  }, [location, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setShowGoogleLogin(false);

    // Kiểm tra mật khẩu khớp nhau
    if (password !== confirmPassword) {
      setError(t('passwordMismatch', { ns: 'auth' }));
      return;
    }

    // Kiểm tra độ dài mật khẩu
    if (password.length < 6) {
      setError(t('passwordTooShort', { ns: 'auth' }));
      return;
    }

    setIsLoading(true);

    try {
      // Gọi service đăng ký
      const response = await register({
        email,
        password,
        full_name: fullName,
      });
      
      // Lưu thông tin session vào localStorage nếu có
      if (response.session) {
        localStorage.setItem('token', response.session.access_token);
      }
      
      // Chuyển hướng về trang chủ hoặc trang xác nhận email
      navigate('/register-success');
    } catch (err: any) {
      console.error('Lỗi đăng ký chi tiết:', err);
      
      // Xử lý các loại lỗi cụ thể
      if (err.message === 'databaseError') {
        setError(t('databaseError', { ns: 'auth' }));
        setShowGoogleLogin(true); // Hiển thị tùy chọn đăng nhập bằng Google
      } else if (err.message === 'emailInUse' || err.message === 'emailInvalid') {
        setError(t(err.message, { ns: 'auth' }));
      } else if (err.message === 'passwordTooShort') {
        setError(t('passwordTooShort', { ns: 'auth' }));
      } else if (err.message === 'invalidDisplayName' || err.message === 'displayNameContainsInvalidChars') {
        setError(t(err.message, { ns: 'auth' }));
      } else if (err.message && typeof err.message === 'string') {
        if (err.message.includes('already registered') || err.message.includes('unique constraint')) {
          setError(t('emailInUse', { ns: 'auth' }));
        } else if (err.message.includes('role')) {
          setError(t('invalidRole', { ns: 'auth' }));
        } else if (err.message.includes('Database error')) {
          setError(t('databaseError', { ns: 'auth' }));
          setShowGoogleLogin(true); // Hiển thị tùy chọn đăng nhập bằng Google
        } else {
          setError(err.message || t('registrationFailed', { ns: 'auth' }));
        }
      } else if (err.code === '23505') { // Postgres unique constraint violation
        setError(t('emailInUse', { ns: 'auth' }));
      } else {
        setError(t('registrationFailed', { ns: 'auth' }));
        setShowGoogleLogin(true); // Hiển thị tùy chọn đăng nhập bằng Google khi không xác định rõ lỗi
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Xử lý đăng nhập bằng Google
  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
      // Không cần chuyển hướng ở đây, Supabase sẽ tự chuyển đến callback URL
    } catch (error) {
      console.error('Lỗi đăng nhập Google:', error);
      setError(t('googleLoginError', { ns: 'auth' }));
    }
  };

  return (
    <div className="container flex items-center justify-center min-h-[calc(100vh-4rem)] py-8">
      <SEO 
        title="Đăng ký | Giác Ngộ"
        description="Tạo tài khoản mới trên Giác Ngộ để truy cập các khóa học Phật giáo, sơ đồ trực quan và công cụ học tập tương tác"
        ogType="website"
      />
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            {t('register', { ns: 'auth' })}
          </CardTitle>
          <CardDescription className="text-center">
            {t('registerDescription', { ns: 'auth' })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {showGoogleLogin && (
            <Alert className="mb-4 bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-amber-700">
                {t('serverIssue', { ns: 'auth', defaultValue: 'Hệ thống đăng ký đang gặp vấn đề. Vui lòng thử đăng nhập bằng Google.' })}
              </AlertDescription>
              <Button 
                variant="outline" 
                className="w-full mt-3 bg-white border-gray-300 hover:bg-gray-50"
                onClick={handleGoogleLogin}
              >
                <img src="/assets/images/google-logo.svg" alt="Google" className="w-5 h-5 mr-2" />
                {t('loginWithGoogle', { ns: 'auth' })}
              </Button>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">{t('displayName', { ns: 'auth' })}</Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('email', { ns: 'auth' })}</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('password', { ns: 'auth' })}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('confirmPassword', { ns: 'auth' })}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  {t('common.loading')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  {t('register', { ns: 'auth' })}
                </span>
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  {t('orContinueWith', { ns: 'auth' })}
                </span>
              </div>
            </div>

            <Button 
              variant="outline" 
              className="w-full mt-3 bg-white border-gray-300 hover:bg-gray-50"
              onClick={handleGoogleLogin}
            >
              <img src="/assets/images/google-logo.svg" alt="Google" className="w-5 h-5 mr-2" />
              {t('loginWithGoogle', { ns: 'auth' })}
            </Button>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <div className="text-center text-sm">
            {t('hasAccount', { ns: 'auth' })}{' '}
            <Button
              variant="link"
              className="p-0 h-auto"
              onClick={() => navigate('/login')}
            >
              {t('login', { ns: 'auth' })}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
} 